// User profile component
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext.js';
import './UserProfile.css';

export default function UserProfile({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { currentUser, playerData, logout, updatePlayerName, loading } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  if (!isOpen || !currentUser) return null;

  const handleEditName = () => {
    setNewPlayerName(playerData?.playername || '');
    setIsEditing(true);
  };

  const handleSaveName = async () => {
    if (!newPlayerName.trim()) {
      alert(t('profile.errors.nameRequired'));
      return;
    }

    setIsUpdating(true);
    const result = await updatePlayerName(newPlayerName.trim());

    if (result.success) {
      setIsEditing(false);
    } else {
      alert(result.error || t('profile.errors.updateFailed'));
    }

    setIsUpdating(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewPlayerName('');
  };

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      onClose();
    } else {
      alert(result.error || t('profile.errors.logoutFailed'));
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get level status data (new three-state system: won, failed, not_done)
  const levelStatusEn = playerData?.levelStatus_en || {};
  const levelStatusDa = playerData?.levelStatus_da || {};

  // Calculate won levels count
  const wonLevelsEn = Object.values(levelStatusEn).filter(status => status === 'won').length;
  const wonLevelsDa = Object.values(levelStatusDa).filter(status => status === 'won').length;

  // Calculate current level (highest reached level + 1)
  const calculateCurrentLevel = (levelStatus) => {
    let highestReachedLevel = 0;
    for (let level = 1; level <= 100; level++) {
      const status = levelStatus[level];
      if (status === 'won' || status === 'failed') {
        highestReachedLevel = level;
      }
    }
    return Math.min(highestReachedLevel + 1, 100);
  };

  const currentLevelEn = calculateCurrentLevel(levelStatusEn);
  const currentLevelDa = calculateCurrentLevel(levelStatusDa);

  // Calculate completion percentages (based on won levels)
  const completionPercentageEn = Math.round((wonLevelsEn / 100) * 100);
  const completionPercentageDa = Math.round((wonLevelsDa / 100) * 100);

  // Helper function to render level grid
  const renderLevelGrid = (language, levelStatus, currentLevel) => {
    const levels = [];
    for (let i = 1; i <= 100; i++) {
      const status = levelStatus[i] || 'not_done';
      const isCurrent = i === currentLevel;

      let className = 'level-badge';
      if (status === 'won') {
        className += ' completed';
      } else if (status === 'failed') {
        className += ' failed';
      } else if (isCurrent) {
        className += ' current';
      } else {
        className += ' locked';
      }

      levels.push(
        <div key={i} className={className} title={`Level ${i} - ${status === 'won' ? 'Won' : status === 'failed' ? 'Failed' : isCurrent ? 'Current' : 'Not attempted'}`}>
          <span className="level-number">{i}</span>
          {status === 'won' && <span className="level-check">✓</span>}
          {status === 'failed' && <span className="level-failed-indicator">✗</span>}
          {isCurrent && status === 'not_done' && <span className="level-current-indicator">▶</span>}
        </div>
      );
    }
    return levels;
  };

  return (
    <div className="user-profile-backdrop" onClick={handleBackdropClick}>
      <div className="user-profile-modal">
        <div className="user-profile-header">
          <h2>{t('profile.title')}</h2>
          <button
            className="profile-close-btn"
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        <div className="user-profile-content">
          {/* Player Name Section */}
          <div className="profile-section">
            <label>{t('profile.playerName')}</label>
            {isEditing ? (
              <div className="edit-name-container">
                <input
                  type="text"
                  value={newPlayerName}
                  onChange={(e) => setNewPlayerName(e.target.value)}
                  disabled={isUpdating}
                  maxLength={30}
                  placeholder={t('profile.placeholders.playerName')}
                />
                <div className="edit-name-buttons">
                  <button
                    onClick={handleSaveName}
                    disabled={isUpdating}
                    className="save-btn"
                  >
                    {isUpdating ? t('profile.saving') : t('profile.save')}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isUpdating}
                    className="cancel-btn"
                  >
                    {t('profile.cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="display-name-container">
                <span className="player-name">
                  {playerData?.playername || t('profile.noNameSet')}
                </span>
                <button
                  onClick={handleEditName}
                  disabled={loading}
                  className="edit-btn"
                >
                  {t('profile.edit')}
                </button>
              </div>
            )}
          </div>

          {/* Email Section */}
          <div className="profile-section">
            <label>{t('profile.email')}</label>
            <span className="profile-value">{currentUser.email}</span>
          </div>

          {/* English Levels Section */}
          <div className="profile-section levels-section">
            <div className="levels-header">
              <label>{t('profile.englishLevels')}</label>
              <div className="completion-stats">
                <span className="completion-count">{wonLevelsEn}/100</span>
                <div className="progress-bar">
                  <div
                    className="progress-fill progress-fill-en"
                    style={{ width: `${completionPercentageEn}%` }}
                  />
                </div>
                <span className="completion-percentage">{completionPercentageEn}%</span>
              </div>
            </div>
            <div className="levels-grid">
              {renderLevelGrid('en', levelStatusEn, currentLevelEn)}
            </div>
          </div>

          {/* Danish Levels Section */}
          <div className="profile-section levels-section">
            <div className="levels-header">
              <label>{t('profile.danishLevels')}</label>
              <div className="completion-stats">
                <span className="completion-count">{wonLevelsDa}/100</span>
                <div className="progress-bar">
                  <div
                    className="progress-fill progress-fill-da"
                    style={{ width: `${completionPercentageDa}%` }}
                  />
                </div>
                <span className="completion-percentage">{completionPercentageDa}%</span>
              </div>
            </div>
            <div className="levels-grid">
              {renderLevelGrid('da', levelStatusDa, currentLevelDa)}
            </div>
          </div>
        </div>

        <div className="user-profile-footer">
          <button
            onClick={handleLogout}
            disabled={loading}
            className="logout-btn"
          >
            {loading ? t('profile.signingOut') : t('profile.signOut')}
          </button>
        </div>
      </div>
    </div>
  );
}
